import i18next from 'i18next';
import React from 'react';
import { initReactI18next, useTranslation as useI18NTranslation } from 'react-i18next';
import { text as enSG } from '../i18n-source/en-sg';
import  { text as thTH } from '../i18n-source/th-th';
import  { text as zhHK } from '../i18n-source/zh-hk';
import  { text as zhTW } from '../i18n-source/zh-tw';
import type { StringMap, TOptions } from 'i18next';
import { ELang } from '@/interfaces/common';
import { countryCode, ECountryCode } from '@/lib/locale';

type DeepPartial<T> = {
    // eslint-disable-next-line @typescript-eslint/ban-types
    [P in keyof T]: T[P] extends string ? T[P] : T[P] extends Function ? T[P] | undefined : DeepPartial<T[P]>;
};

type LangTextType = DeepPartial<
        typeof enSG &
        typeof thTH &
        typeof zhHK &
        typeof zhTW
>;

declare global {
    interface Window {
        i18next: typeof i18next;
    }
}

type TFunction<T extends Record<string, any>> = <
    K extends keyof T,
    TResult = T[K],
    TInterpolationMap extends Record<string, any> = StringMap
>(
    key: K,
    options?: TOptions<TInterpolationMap>,
) => TResult;

window.i18next = i18next;

export const namespace = 'globalTranslator';

i18next.use(initReactI18next).init({
  resources: { 'en-sg': { ns: {} } },
  lng: countryCode === ECountryCode.TW ? ELang.TW : ELang.EN,
  lowerCaseLng: true,
  fallbackLng: ELang.EN,
  interpolation: {
    escapeValue: false,
  },
});

i18next.addResourceBundle('en-sg', namespace, enSG, true, true);
switch (countryCode){
    case ECountryCode.HK:
        i18next.addResourceBundle('zh-hk', namespace, zhHK, true, true);
        break;
    case ECountryCode.TW:
        i18next.addResourceBundle('zh-tw', namespace, zhTW, true, true);
        break;
    case ECountryCode.TH:
        i18next.addResourceBundle('th-th', namespace, thTH, true, true);
        break;
}

// hooks for translation
export const useTranslation = () => {
    const tFunction = useI18NTranslation(namespace).t;
    const t: TFunction<LangTextType> = React.useCallback(
        (field: string, options?: TOptions<StringMap>) =>
            tFunction(
                field,
                Object.assign({ returnObjects: true }, { ...options }, { lng: i18next.language } as TOptions),
            ) as any,
        [tFunction],
    );
    return t;
};

// hooks for module translation
export const useModuleTranslation: <T extends keyof LangTextType>(subModule: T) => LangTextType[T] = (subModule) => {
    const t = useTranslation();
    const text = React.useMemo(() => t(subModule, { returnObjects: true }), [subModule, t]);
    return text;
};

export const getTranslator: () => <T extends keyof LangTextType>(
    field: T,
    options?: TOptions<StringMap>,
) => LangTextType[T] = () => (field, options) => {
    return i18next.t(
        field,
        Object.assign({ returnObjects: true }, { ...options }, {
            lng: i18next.language,
            ns: namespace,
        } as TOptions),
    );
};

export const getModuleTranslator: <T extends keyof LangTextType>(subModule: T) => LangTextType[T] = (subModule) => {
    const t = (field: string, options?: TOptions<StringMap>) => {
        return i18next.t(
            field,
            Object.assign({ returnObjects: true }, { ...options }, {
                lng: i18next.language,
                ns: namespace,
            } as TOptions),
        );
    };
    const text = t(subModule, { returnObjects: true }) as any;
    return text;
};
